// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// WebAuthn/Passkey Utility — Biometric Login System
// ================================================================

/**
 * WebAuthn Passkey Manager
 *
 * Handles fingerprint/FaceID registration and authentication.
 * Works with any browser that supports WebAuthn (Chrome, Safari, Edge, Firefox).
 *
 * Flow:
 *   1. User logs in with password ONCE
 *   2. App offers "Set up fingerprint login"
 *   3. User scans fingerprint → credential stored in browser + DB
 *   4. Next login → user just touches fingerprint sensor
 *   5. Password stays as fallback
 */

// Convert ArrayBuffer to base64url string
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// Convert base64url string to ArrayBuffer
function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  const padded = pad ? base64 + '='.repeat(4 - pad) : base64;
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

// Generate a random challenge
function generateChallenge() {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return arr;
}

// Detect device name for friendly labeling
function getDeviceName() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown Device';
}

/**
 * Check if WebAuthn/Passkeys are supported on this device
 */
export function isPasskeySupported() {
  return !!(window.PublicKeyCredential &&
    typeof window.PublicKeyCredential === 'function');
}

/**
 * Check if platform authenticator (fingerprint/FaceID) is available
 */
export async function isBiometricAvailable() {
  if (!isPasskeySupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Register a new passkey (fingerprint/FaceID) for the current user.
 * Call this AFTER the user is already logged in with password.
 *
 * @param {object} params
 * @param {string} params.userId - Supabase auth user ID
 * @param {string} params.userEmail - User's email
 * @param {string} params.userName - User's display name
 * @param {object} params.supabase - Supabase client instance
 * @returns {object} { success, error, credentialId }
 */
export async function registerPasskey({ userId, userEmail, userName, supabase }) {
  try {
    // Check for existing passkeys to exclude them
    const { data: existing } = await supabase
      .from('user_passkeys')
      .select('credential_id')
      .eq('user_id', userId);

    const excludeCredentials = (existing || []).map(p => ({
      type: 'public-key',
      id: base64urlToBuffer(p.credential_id),
    }));

    const challenge = generateChallenge();

    const createOptions = {
      publicKey: {
        rp: {
          name: 'Miskulin Technologies',
          id: window.location.hostname,
        },
        user: {
          id: new TextEncoder().encode(userId),
          name: userEmail,
          displayName: userName || userEmail.split('@')[0],
        },
        challenge,
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },   // ES256
          { type: 'public-key', alg: -257 },  // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform', // Force fingerprint/FaceID, not USB keys
          userVerification: 'required',
          residentKey: 'preferred',
        },
        timeout: 60000,
        excludeCredentials,
        attestation: 'none',
      },
    };

    const credential = await navigator.credentials.create(createOptions);

    if (!credential) {
      return { success: false, error: 'Registration was cancelled.' };
    }

    const credentialId = bufferToBase64url(credential.rawId);
    const publicKey = bufferToBase64url(credential.response.getPublicKey());
    const transports = credential.response.getTransports
      ? credential.response.getTransports()
      : ['internal'];
    const deviceName = getDeviceName();

    // Store in Supabase
    const { error: dbError } = await supabase
      .from('user_passkeys')
      .insert({
        user_id: userId,
        credential_id: credentialId,
        public_key: publicKey,
        counter: 0,
        device_name: deviceName,
        transports,
      });

    if (dbError) {
      console.error('[Passkey] DB save failed:', dbError);
      return { success: false, error: 'Failed to save passkey. Try again.' };
    }

    // Also store credential ID locally for faster lookup
    const stored = JSON.parse(localStorage.getItem('mt_passkeys') || '[]');
    stored.push({ credentialId, userEmail, deviceName });
    localStorage.setItem('mt_passkeys', JSON.stringify(stored));

    return { success: true, credentialId, deviceName };
  } catch (err) {
    console.error('[Passkey] Registration error:', err);
    if (err.name === 'NotAllowedError') {
      return { success: false, error: 'Biometric registration was denied or timed out.' };
    }
    if (err.name === 'InvalidStateError') {
      return { success: false, error: 'This fingerprint is already registered.' };
    }
    return { success: false, error: err.message || 'Passkey registration failed.' };
  }
}

/**
 * Authenticate using a registered passkey (fingerprint/FaceID).
 * Call this from the login page INSTEAD of password.
 *
 * @param {object} params
 * @param {object} params.supabase - Supabase client instance
 * @returns {object} { success, email, error }
 */
export async function authenticateWithPasskey({ supabase }) {
  try {
    // Check if we have any stored credential hints
    const stored = JSON.parse(localStorage.getItem('mt_passkeys') || '[]');
    const allowCredentials = stored.length > 0
      ? stored.map(p => ({
          type: 'public-key',
          id: base64urlToBuffer(p.credentialId),
          transports: ['internal'],
        }))
      : []; // Empty = browser shows all available passkeys

    const challenge = generateChallenge();

    const getOptions = {
      publicKey: {
        challenge,
        rpId: window.location.hostname,
        allowCredentials,
        userVerification: 'required',
        timeout: 60000,
      },
    };

    const assertion = await navigator.credentials.get(getOptions);

    if (!assertion) {
      return { success: false, error: 'Authentication was cancelled.' };
    }

    const credentialId = bufferToBase64url(assertion.rawId);

    // Look up this credential in the database
    const { data: passkey, error: lookupError } = await supabase
      .from('user_passkeys')
      .select('*, auth_email:user_id')
      .eq('credential_id', credentialId)
      .single();

    if (lookupError || !passkey) {
      return { success: false, error: 'Passkey not recognized. Use password to sign in.' };
    }

    // Update counter and last_used_at
    await supabase
      .from('user_passkeys')
      .update({
        counter: passkey.counter + 1,
        last_used_at: new Date().toISOString(),
      })
      .eq('id', passkey.id);

    // Now we need to get the user's email from Supabase auth
    // We can't directly query auth.users with anon key, so we look it up
    // from our stored credentials or the users table
    const { data: userRecord } = await supabase
      .from('users')
      .select('email')
      .eq('auth_user_id', passkey.user_id)
      .single();

    // Fallback to local storage
    const email = userRecord?.email
      || stored.find(s => s.credentialId === credentialId)?.userEmail
      || null;

    if (!email) {
      return { success: false, error: 'Could not determine account. Use password to sign in.' };
    }

    return { success: true, email, userId: passkey.user_id, credentialId };
  } catch (err) {
    console.error('[Passkey] Auth error:', err);
    if (err.name === 'NotAllowedError') {
      return { success: false, error: 'Biometric scan was denied or timed out.' };
    }
    return { success: false, error: err.message || 'Passkey authentication failed.' };
  }
}

/**
 * Get list of registered passkeys for the current user
 */
export async function getRegisteredPasskeys({ userId, supabase }) {
  const { data, error } = await supabase
    .from('user_passkeys')
    .select('id, credential_id, device_name, created_at, last_used_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) return [];
  return data || [];
}

/**
 * Delete a passkey
 */
export async function deletePasskey({ passkeyId, supabase }) {
  const { error } = await supabase
    .from('user_passkeys')
    .delete()
    .eq('id', passkeyId);

  if (error) return { success: false, error: error.message };

  // Also clean up local storage
  const stored = JSON.parse(localStorage.getItem('mt_passkeys') || '[]');
  localStorage.setItem('mt_passkeys', JSON.stringify(
    stored.filter(s => s.passkeyId !== passkeyId)
  ));

  return { success: true };
}

/**
 * Check if current user has any passkeys registered
 */
export function hasLocalPasskeys() {
  const stored = JSON.parse(localStorage.getItem('mt_passkeys') || '[]');
  return stored.length > 0;
}
